import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Wind, Brain, Heart, Users, 
  Sparkles, Music, Eye, MessageCircle, Compass, 
  Activity, Waves, Moon, Sun, Shield, Zap
} from 'lucide-react';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/PublicHeader';

const Tools = () => {
  const { isAuthenticated } = useAuth();
  
  const toolCategories = [
    {
      title: "Breathing & Body",
      description: "Ground yourself through breath and body awareness",
      icon: Wind,
      color: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
      tools: [
        { name: "Box Breathing", description: "4-4-4-4 pattern for instant calm", icon: Wind },
        { name: "Breathing Patterns", description: "Various techniques for different needs", icon: Waves },
        { name: "Body Scan", description: "Notice and release tension", icon: Eye },
        { name: "Progressive Muscle Relaxation", description: "Systematic tension release", icon: Activity }
      ]
    },
    {
      title: "Guided Meditations",
      description: "Audio-guided journeys for deep regulation",
      icon: Music,
      color: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
      tools: [
        { name: "Breathing Orb Meditation", description: "Visual breathing with calming guidance", icon: Moon },
        { name: "Still Sphere", description: "Find your center of calm", icon: Sun },
        { name: "EMDR/Bilateral Regulation", description: "Rhythmic, alternating sensory input to calm & ground", icon: Eye },
        { name: "Somatic Experiencing", description: "Body-based trauma-informed practice", icon: Heart },
        { name: "Hypnotherapy Sessions", description: "Deep relaxation and rewiring", icon: Sparkles }
      ]
    },
    {
      title: "Emotional Tools",
      description: "Understand and process your emotions",
      icon: Heart,
      color: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400",
      tools: [
        { name: "Emotion Identifier", description: "Name what you're feeling", icon: Heart },
        { name: "Regulation Navigator", description: "Find the right tool for your state", icon: Compass },
        { name: "Self-Talk Reframes", description: "Transform negative thought patterns", icon: Brain },
        { name: "Micro Reset", description: "Quick 30-second regulation", icon: Zap }
      ]
    },
    {
      title: "Relationship Tools",
      description: "Navigate ADHD in relationships",
      icon: Users,
      color: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
      tools: [
        { name: "Relationship Check-In", description: "Structured partner conversations", icon: Users },
        { name: "Attachment Needs", description: "Understand your attachment style", icon: Heart },
        { name: "Break the Cycle", description: "Interrupt negative patterns", icon: Shield },
        { name: "Repair & Reconnection", description: "Tools for after conflict", icon: Sparkles }
      ]
    },
    {
      title: "AI Companion",
      description: "24/7 support that understands ADHD",
      icon: MessageCircle,
      color: "bg-primary/10 text-primary",
      tools: [
        { name: "Chat Support", description: "Talk through difficult moments", icon: MessageCircle },
        { name: "Personalized Guidance", description: "Recommendations based on your patterns", icon: Compass },
        { name: "Crisis Support", description: "Immediate help when overwhelmed", icon: Shield },
        { name: "Progress Insights", description: "Understand your regulation journey", icon: Brain }
      ]
    }
  ];

  // SEO Schema - ItemList
  useSchema({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "CoRegulate ADHD Regulation Tools",
    "description": "Complete toolkit for ADHD emotional regulation including breathing exercises, guided meditations, emotional tools, and AI support",
    "url": "https://coregulateadhd.com/tools",
    "numberOfItems": 5,
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Breathing & Body", "description": "Ground yourself through breath and body awareness" },
      { "@type": "ListItem", "position": 2, "name": "Guided Meditations", "description": "Audio-guided journeys for deep regulation" },
      { "@type": "ListItem", "position": 3, "name": "Emotional Tools", "description": "Understand and process your emotions" },
      { "@type": "ListItem", "position": 4, "name": "Relationship Tools", "description": "Navigate ADHD in relationships" },
      { "@type": "ListItem", "position": 5, "name": "AI Companion", "description": "24/7 support that understands ADHD" }
    ]
  });

  return (
    <>
      <Helmet>
        <title>Regulation Tools | CoRegulate - ADHD Emotional Regulation App</title>
        <meta name="description" content="Explore CoRegulate's complete toolkit for ADHD emotional regulation: breathing exercises, guided meditations, emotion tools, relationship aids, and AI support." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/tools" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ADHD Regulation Tools | CoRegulate" />
        <meta property="og:description" content="50+ tools for ADHD emotional regulation: breathing exercises, guided meditations, relationship aids, and 24/7 AI support." />
        <meta property="og:url" content="https://coregulateadhd.com/tools" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ADHD Regulation Tools | CoRegulate" />
        <meta name="twitter:description" content="50+ tools for ADHD emotional regulation: breathing exercises, guided meditations, relationship aids, and 24/7 AI support." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
        {/* Header - Matching site style */}
        <PublicHeader currentPage="tools" />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-serif text-[#1D3D3D] mb-6">
                Regulation <span className="text-[#1D6B6B]">Tools</span>
              </h1>
              <p className="text-lg md:text-xl text-[#5A7A7A] max-w-2xl mx-auto">
                A complete toolkit designed for ADHD minds. From quick resets to deep meditations — 
                find what works for you in any moment.
              </p>
            </div>
          </section>

          {/* Quick Stats */}
          <section className="pb-12 px-4">
            <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] text-center shadow-sm">
                <div className="text-2xl font-bold text-[#1D6B6B]">50+</div>
                <div className="text-xs text-[#7D8E95]">Tools Available</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] text-center shadow-sm">
                <div className="text-2xl font-bold text-[#1D6B6B]">5</div>
                <div className="text-xs text-[#7D8E95]">Categories</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] text-center shadow-sm">
                <div className="text-2xl font-bold text-[#1D6B6B]">1-15</div>
                <div className="text-xs text-[#7D8E95]">Minutes Each</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E8E1D6] text-center shadow-sm">
                <div className="text-2xl font-bold text-[#1D6B6B]">24/7</div>
                <div className="text-xs text-[#7D8E95]">AI Support</div>
              </div>
            </div>
          </section>

          {/* Tool Categories */}
          <section className="py-12 px-4">
            <div className="max-w-6xl mx-auto space-y-12">
              {toolCategories.map((category) => (
                <div key={category.title} className="bg-white rounded-2xl border border-[#E8E1D6] overflow-hidden shadow-sm">
                  {/* Category Header */}
                  <div className="p-6 md:p-8 border-b border-[#E8E1D6]">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${category.color}`}>
                        <category.icon className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-serif text-[#1D3D3D]">{category.title}</h2>
                        <p className="text-[#5A7A7A]">{category.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tools Grid */}
                  <div className="p-6 md:p-8 bg-white/70/30">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {category.tools.map((tool) => (
                        <div 
                          key={tool.name}
                          className="bg-white rounded-xl p-4 border border-[#E8E1D6] hover:border-[#1D6B6B]/50 transition-colors shadow-sm"
                        >
                          <div className="w-10 h-10 bg-[#1D6B6B]/10 rounded-lg flex items-center justify-center mb-3">
                            <tool.icon className="w-5 h-5 text-[#1D6B6B]" />
                          </div>
                          <h3 className="font-medium text-[#1D3D3D] mb-1">{tool.name}</h3>
                          <p className="text-xs text-[#5A7A7A]">{tool.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How Tools Are Different */}
          <section className="py-16 px-4 bg-white/50">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-8">
                Why Our Tools Work for ADHD
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 border border-[#E8E1D6] shadow-sm">
                  <Zap className="w-8 h-8 text-[#1D6B6B] mx-auto mb-3" />
                  <h3 className="font-semibold text-[#1D3D3D] mb-2">Quick & Effective</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    Most tools take 1-5 minutes. Get regulated without losing focus.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-[#E8E1D6] shadow-sm">
                  <Music className="w-8 h-8 text-[#1D6B6B] mx-auto mb-3" />
                  <h3 className="font-semibold text-[#1D3D3D] mb-2">Audio Guided</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    Close your eyes and follow along. No reading required.
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 border border-[#E8E1D6] shadow-sm">
                  <Compass className="w-8 h-8 text-[#1D6B6B] mx-auto mb-3" />
                  <h3 className="font-semibold text-[#1D3D3D] mb-2">Smart Suggestions</h3>
                  <p className="text-sm text-[#5A7A7A]">
                    Tell us how you feel, and we'll suggest the right tool.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-4">
                Ready to Explore?
              </h2>
              <p className="text-[#5A7A7A] mb-8">
                Create a free account to access our full toolkit. Start with what feels 
                right and discover what works for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/auth" 
                  className="inline-flex items-center justify-center gap-2 text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-all shadow-md"
                  style={{
                      background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                  }}
                >
                  Start for Free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/how-it-works" 
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1D3D3D] px-8 py-3 rounded-full font-medium border border-[#E8E1D6] hover:bg-white/70 transition-colors"
                >
                  How It Works
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-white/80 border-t border-[#E8E1D6]">
          <div className="max-w-6xl mx-auto px-4 py-8 text-center space-x-6">
            <Link to="/about" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              About
            </Link>
            <Link to="/how-it-works" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              How It Works
            </Link>
            <Link to="/pricing" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Pricing
            </Link>
            <Link to="/contact" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Contact
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Tools;
